import { createContext, useContext, ReactNode, useMemo } from "react";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";

interface RealtimeSyncContextType {
  favorites: string[];
  favoriteVegetableOils: string[];
  favoriteNeutralBases: string[];
  favoriteSupplies: string[];
  cabinetOils: string[];
  cabinetVegetableOils: string[];
  cabinetNeutralBases: string[];
  cabinetSupplies: string[];
  isConnected: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  addFavorite: (oilId: string) => Promise<void>;
  removeFavorite: (oilId: string) => Promise<void>;
  addFavoriteByType: (itemId: string, itemType: string) => Promise<void>;
  removeFavoriteByType: (itemId: string, itemType: string) => Promise<void>;
  getFavoritesByType: (itemType: string) => string[];
  addToCabinet: (oilId: string) => Promise<void>;
  removeFromCabinet: (oilId: string) => Promise<void>;
  addToCabinetByType: (itemId: string, itemType: string) => Promise<void>;
  removeFromCabinetByType: (itemId: string, itemType: string) => Promise<void>;
  getCabinetByType: (itemType: string) => string[];
  resetToLocal: () => void;
}

const RealtimeSyncContext = createContext<RealtimeSyncContextType | undefined>(undefined);

export function RealtimeSyncProvider({ children }: { children: ReactNode }) {
  const syncData = useRealtimeSync();

  // Memoize only on state/flag changes — action callbacks are stable via
  // useCallback in useRealtimeSync and update only when state changes do.
  // This prevents cascading re-renders across all consumers on every Provider render.
  const value = useMemo(
    () => ({ ...syncData }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      syncData.favorites,
      syncData.favoriteVegetableOils,
      syncData.favoriteNeutralBases,
      syncData.favoriteSupplies,
      syncData.cabinetOils,
      syncData.cabinetVegetableOils,
      syncData.cabinetNeutralBases,
      syncData.cabinetSupplies,
      syncData.isConnected,
      syncData.isLoading,
      syncData.isAuthenticated,
    ]
  );

  return (
    <RealtimeSyncContext.Provider value={value}>
      {children}
    </RealtimeSyncContext.Provider>
  );
}

export function useRealtimeSyncContext() {
  const context = useContext(RealtimeSyncContext);
  if (context === undefined) {
    throw new Error("useRealtimeSyncContext must be used within a RealtimeSyncProvider");
  }
  return context;
}
